import {
  ArrowCounterClockwiseIcon,
  DownloadSimpleIcon,
  LockKeyIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { useEffect, useMemo, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { AnalysisWarning, GitignoreSourceInput } from '../types';
import { copyText, downloadText, exportJson, exportMarkdown } from '../core/export';
import {
  FileInputError,
  loadGitignoreFile,
  loadProjectDirectory,
  parsePastedPaths,
  type DirectoryLoadProgress,
} from '../core/io';
import { useAnalysis } from '../hooks/useAnalysis';
import { sampleProject } from '../samples';
import { translate, useI18n, type Locale } from '../i18n';
import { ExportDialog, type ExportAction } from '../components/ExportDialog';
import { FilesPanel } from '../components/FilesPanel';
import { Footer } from '../components/Footer';
import { Header } from '../components/Header';
import { PasteDialog } from '../components/PasteDialog';
import { RulesPanel } from '../components/RulesPanel';
import { SocialPreview } from '../components/SocialPreview';
import { WhyPanel } from '../components/WhyPanel';
import { applyTheme, readStoredTheme, storeTheme, type Theme } from './theme';

type MobilePanel = 'rules' | 'files' | 'why';
type DialogName = 'paste' | 'export' | null;
const MOBILE_PANELS: readonly MobilePanel[] = ['rules', 'files', 'why'];

interface UndoState {
  readonly sources: readonly GitignoreSourceInput[];
  readonly ignoredBefore: number;
  readonly label: string;
}

function gitignoreErrorKey(error: unknown): string {
  if (!(error instanceof FileInputError)) {
    return 'errors.gitignoreLoad';
  }

  switch (error.issues[0]?.code) {
    case 'not-gitignore':
      return 'errors.notGitignore';
    case 'binary-gitignore':
      return 'errors.binaryGitignore';
    case 'invalid-encoding':
      return 'errors.invalidEncoding';
    case 'gitignore-too-large':
      return 'errors.gitignoreTooLarge';
    case 'read-failed':
      return 'errors.readFailed';
    default:
      return 'errors.gitignoreLoad';
  }
}

function activeSourceContent(sources: readonly GitignoreSourceInput[], path: string): string {
  return sources.find((source) => source.path === path)?.content ?? '';
}

export function App() {
  const { locale, setLocale, t } = useI18n();
  const [sources, setSources] = useState<readonly GitignoreSourceInput[]>(sampleProject.sources);
  const [paths, setPaths] = useState<readonly string[]>(sampleProject.paths);
  const [pathDraft, setPathDraft] = useState(sampleProject.paths.join('\n'));
  const [projectName, setProjectName] = useState(sampleProject.projectName);
  const [selectedSource, setSelectedSource] = useState(
    sampleProject.sources[0]?.path ?? '.gitignore',
  );
  const [selectedPath, setSelectedPath] = useState<string | null>(sampleProject.selectedPath);
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
  const [theme, setTheme] = useState<Theme>(() => readStoredTheme());
  const [mobilePanel, setMobilePanel] = useState<MobilePanel>('files');
  const [dialog, setDialog] = useState<DialogName>(null);
  const [eventAnnouncement, setEventAnnouncement] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [undo, setUndo] = useState<UndoState | null>(null);
  const [folderProgress, setFolderProgress] = useState<DirectoryLoadProgress | null>(null);

  const analysisInput = useMemo(() => ({ sources, paths }), [paths, sources]);
  const { report, status, progress, error, errorCode, fallbackUsed, rerun } =
    useAnalysis(analysisInput);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const selectedSourceContent = activeSourceContent(sources, selectedSource);
  const isSocialPreview = new URLSearchParams(window.location.search).has('social-preview');
  const folderSupported = 'webkitdirectory' in document.createElement('input');
  const effectiveSelectedPath =
    report && selectedPath && report.paths.some((entry) => entry.path === selectedPath)
      ? selectedPath
      : (report?.paths.find((entry) => entry.kind === 'file')?.path ?? null);
  const impact =
    undo && report ? { before: undo.ignoredBefore, after: report.summary.ignoredCount } : null;
  const announcement = folderProgress
    ? t('progress.folder', {
        completed: folderProgress.processed,
        total: folderProgress.total,
      })
    : impact
      ? t('announcements.suggestionApplied', impact)
      : status === 'running'
        ? t('announcements.analyzing', { progress })
        : status === 'complete' && report
          ? t('announcements.complete', { count: report.summary.pathCount })
          : eventAnnouncement;

  function handleLocaleChange(nextLocale: Locale): void {
    if (!setLocale(nextLocale)) {
      setNotice(translate(nextLocale, 'errors.storageUnavailable'));
    }
  }

  function toggleTheme(): void {
    const nextTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(nextTheme);
    if (!storeTheme(nextTheme)) {
      setNotice(t('errors.storageUnavailable'));
    }
  }

  function handleMobileTabKeyDown(
    event: ReactKeyboardEvent<HTMLButtonElement>,
    panel: MobilePanel,
  ): void {
    const currentIndex = MOBILE_PANELS.indexOf(panel);
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % MOBILE_PANELS.length;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + MOBILE_PANELS.length) % MOBILE_PANELS.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = MOBILE_PANELS.length - 1;
    }
    if (nextIndex === null) {
      return;
    }
    const nextPanel = MOBILE_PANELS[nextIndex];
    if (nextPanel === undefined) {
      return;
    }
    event.preventDefault();
    setMobilePanel(nextPanel);
    document.getElementById(`mobile-tab-${nextPanel}`)?.focus();
  }

  function loadSample(): void {
    setSources(sampleProject.sources);
    setPaths(sampleProject.paths);
    setPathDraft(sampleProject.paths.join('\n'));
    setProjectName(sampleProject.projectName);
    setSelectedSource(sampleProject.sources[0]?.path ?? '.gitignore');
    setSelectedPath(sampleProject.selectedPath);
    setSelectedRuleId(null);
    setUndo(null);
    setEventAnnouncement(t('notices.sampleLoaded'));
    setNotice(t('notices.sampleLoaded'));
  }

  function reset(): void {
    setSources([{ path: '.gitignore', content: '' }]);
    setPaths([]);
    setPathDraft('');
    setProjectName('');
    setSelectedSource('.gitignore');
    setSelectedPath(null);
    setSelectedRuleId(null);
    setUndo(null);
    setEventAnnouncement(t('notices.reset'));
    setNotice(t('notices.reset'));
  }

  async function handleFolder(files: readonly File[]): Promise<void> {
    setFolderProgress({ processed: 0, total: files.length, gitignoreFilesRead: 0 });
    try {
      const loaded = await loadProjectDirectory(files, { onProgress: setFolderProgress });
      const loadedSources =
        loaded.sources.length > 0
          ? loaded.sources
          : [{ path: '.gitignore', content: '' } satisfies GitignoreSourceInput];
      setSources(loadedSources);
      setPaths(loaded.paths);
      setPathDraft(loaded.paths.join('\n'));
      setProjectName(loaded.projectName);
      setSelectedSource(loadedSources[0]?.path ?? '.gitignore');
      setSelectedPath(loaded.paths[0] ?? null);
      setSelectedRuleId(null);
      const skippedIssues = loaded.issues.filter(
        (issue) => issue.code !== 'path-limit-exceeded',
      ).length;
      setNotice(
        loaded.truncated
          ? skippedIssues > 0
            ? t('notices.folderTruncatedWithIssues', {
                count: loaded.paths.length,
                issues: skippedIssues,
              })
            : t('notices.folderTruncated', { count: loaded.paths.length })
          : skippedIssues > 0
            ? t('notices.folderLoadedWithIssues', {
                count: loaded.paths.length,
                issues: skippedIssues,
              })
            : t('notices.folderLoaded', { count: loaded.paths.length }),
      );
    } catch {
      setNotice(t('errors.folderLoad'));
    } finally {
      setFolderProgress(null);
    }
  }

  async function handleGitignore(file: File): Promise<void> {
    try {
      const loaded = await loadGitignoreFile(file);
      setSources([loaded.source]);
      setSelectedSource(loaded.source.path);
      setSelectedRuleId(null);
      setNotice(t('notices.gitignoreLoaded'));
    } catch (loadError) {
      setNotice(t(gitignoreErrorKey(loadError)));
    }
  }

  function applyPastedInputs(gitignore: string, pathText: string): void {
    const parsed = parsePastedPaths(pathText);
    setSources([{ path: '.gitignore', content: gitignore }]);
    setPaths(parsed.paths);
    setPathDraft(pathText);
    setSelectedSource('.gitignore');
    setSelectedPath(parsed.paths[0] ?? null);
    setSelectedRuleId(null);
    setDialog(null);
    setNotice(
      parsed.issues.length > 0
        ? t('notices.pathsWithIssues', { count: parsed.issues.length })
        : t('notices.inputsApplied', { count: parsed.paths.length }),
    );
  }

  function updateSource(path: string, content: string): void {
    setSources((current) =>
      current.map((source) => (source.path === path ? { ...source, content } : source)),
    );
  }

  function applySuggestion(warning: AnalysisWarning): void {
    if (!warning.suggestedRule) return;
    const targetPath = warning.suggestedSource ?? '.gitignore';
    const currentSource = sources.find((source) => source.path === targetPath);
    const original = currentSource?.content ?? '';
    const suffix = original.length > 0 && !original.endsWith('\n') ? '\n' : '';
    const content = `${original}${suffix}${warning.suggestedRule}\n`;
    const snapshot: UndoState = {
      sources,
      ignoredBefore: report?.summary.ignoredCount ?? 0,
      label: warning.message,
    };

    setUndo(snapshot);
    setSources((current) => {
      if (currentSource) {
        return current.map((source) =>
          source.path === targetPath ? { ...source, content } : source,
        );
      }
      return [...current, { path: targetPath, content }];
    });
    setSelectedSource(targetPath);
  }

  function undoSuggestion(): void {
    if (!undo) return;
    setSources(undo.sources);
    setEventAnnouncement(t('announcements.suggestionUndone'));
    setUndo(null);
  }

  async function copy(value: string, successKey: string): Promise<void> {
    try {
      await copyText(value);
      setNotice(t(successKey));
    } catch {
      setNotice(t('errors.copy'));
    }
  }

  function download(filename: string, value: string, mimeType: string): void {
    try {
      downloadText(value, filename, mimeType);
      setNotice(t('notices.downloadStarted'));
    } catch {
      setNotice(t('errors.export'));
    }
  }

  function handleExport(action: ExportAction, includeProject: boolean, name: string): void {
    if (!report) return;
    const options = { includeProjectName: includeProject, projectName: name };
    const isJson = action.endsWith('json');
    const content = isJson ? exportJson(report, options) : exportMarkdown(report, options);
    const filename = isJson ? 'what-the-ignore-report.json' : 'what-the-ignore-report.md';
    const mime = isJson ? 'application/json' : 'text/markdown';
    if (action.startsWith('copy')) void copy(content, 'notices.reportCopied');
    else download(filename, content, mime);
  }

  if (isSocialPreview) {
    return <SocialPreview report={report} selectedPath={sampleProject.selectedPath} />;
  }

  return (
    <div className="app-shell">
      <a className="skip-link" href="#workbench">
        {t('accessibility.skipToWorkbench')}
      </a>
      <Header
        locale={locale}
        theme={theme}
        folderSupported={folderSupported}
        onLocaleChange={handleLocaleChange}
        onThemeChange={toggleTheme}
        onFolderPicked={(files) => void handleFolder(files)}
        onGitignorePicked={(file) => void handleGitignore(file)}
        onOpenPaste={() => {
          setDialog('paste');
        }}
        onLoadSample={loadSample}
        onReset={reset}
      />

      <div className="privacy-strip">
        <span>
          <LockKeyIcon aria-hidden="true" /> <strong>{t('privacy.filesNeverLeave')}</strong>
        </span>
        <span>{t('privacy.noFileContents')}</span>
        <button
          className="text-button export-trigger"
          type="button"
          onClick={() => {
            setDialog('export');
          }}
        >
          <DownloadSimpleIcon aria-hidden="true" /> {t('actions.exportReport')}
        </button>
        <div className="analysis-state" aria-hidden="true">
          <span className={`analysis-indicator status-${folderProgress ? 'running' : status}`} />
          <span>
            {folderProgress
              ? t('progress.folder', {
                  completed: folderProgress.processed,
                  total: folderProgress.total,
                })
              : t(`analysis.${status}`, { progress })}
          </span>
        </div>
      </div>

      {error ? (
        <div className="error-banner" role="alert">
          <WarningIcon aria-hidden="true" />
          <div>
            <strong>{t('errors.workerTitle')}</strong>
            <span>
              {t(
                errorCode === 'worker-unavailable-large-input'
                  ? 'errors.workerLargeInput'
                  : 'errors.analysisFailed',
              )}
            </span>
          </div>
          <button className="button button-small" type="button" onClick={rerun}>
            {t('actions.retry')}
          </button>
        </div>
      ) : null}

      {fallbackUsed && !error ? (
        <div className="undo-banner" role="status">
          <WarningIcon aria-hidden="true" />
          <span>{t('errors.workerFailed')}</span>
        </div>
      ) : null}

      {undo ? (
        <div className="undo-banner" role="status">
          <span>
            {impact
              ? t('notices.suggestionImpact', { before: impact.before, after: impact.after })
              : t('notices.applyingSuggestion')}
          </span>
          <button className="text-button" type="button" onClick={undoSuggestion}>
            <ArrowCounterClockwiseIcon aria-hidden="true" /> {t('actions.undo')}
          </button>
        </div>
      ) : null}

      <div className="mobile-tabs" role="tablist" aria-label={t('mobile.panelTabs')}>
        {MOBILE_PANELS.map((panel) => (
          <button
            id={`mobile-tab-${panel}`}
            key={panel}
            type="button"
            role="tab"
            aria-controls={`panel-${panel}`}
            aria-selected={mobilePanel === panel}
            tabIndex={mobilePanel === panel ? 0 : -1}
            className={mobilePanel === panel ? 'is-active' : ''}
            onKeyDown={(event) => {
              handleMobileTabKeyDown(event, panel);
            }}
            onClick={() => {
              setMobilePanel(panel);
            }}
          >
            {t(`mobile.${panel}`)}
          </button>
        ))}
      </div>

      <main id="workbench" className="workbench" data-mobile-panel={mobilePanel}>
        <RulesPanel
          report={report}
          sources={sources}
          selectedSource={selectedSource}
          selectedRuleId={selectedRuleId}
          onSourceSelect={setSelectedSource}
          onSourceContentChange={updateSource}
          onRuleSelect={setSelectedRuleId}
          onCopySource={() => void copy(selectedSourceContent, 'notices.gitignoreCopied')}
          onDownloadSource={() => {
            download('.gitignore', selectedSourceContent, 'text/plain');
          }}
        />
        <FilesPanel
          report={report}
          selectedPath={effectiveSelectedPath}
          selectedRuleId={selectedRuleId}
          onSelectPath={(path) => {
            setSelectedPath(path);
            setMobilePanel('why');
          }}
        />
        <WhyPanel
          report={report}
          selectedPath={effectiveSelectedPath}
          onApplySuggestion={applySuggestion}
          onCopyCommand={(command) => void copy(command, 'notices.commandCopied')}
        />
      </main>

      <Footer />

      <div className="visually-hidden" aria-live="polite" aria-atomic="true">
        {announcement}
      </div>
      {notice ? (
        <div
          className="toast"
          role="status"
          onAnimationEnd={() => {
            setNotice(null);
          }}
        >
          {notice}
        </div>
      ) : null}

      {dialog === 'paste' ? (
        <PasteDialog
          initialGitignore={sources.find((source) => source.path === '.gitignore')?.content ?? ''}
          initialPaths={pathDraft}
          onApply={applyPastedInputs}
          onClose={() => {
            setDialog(null);
          }}
        />
      ) : null}
      {dialog === 'export' ? (
        <ExportDialog
          defaultProjectName={projectName}
          onExport={handleExport}
          onClose={() => {
            setDialog(null);
          }}
        />
      ) : null}
    </div>
  );
}
